/* ============================================================
   cust-persona-search-drawer.js — inline persona-page search drawer
   ============================================================
   Loaded by the persona-shell section. Intercepts the magnifying-glass
   icon click and, instead of letting the browser navigate to /search,
   opens an in-page drawer that:

   1. Reads the search query as the visitor types.
   2. Hits Shopify's predictive-search endpoint (/search/suggest.json)
      to show product matches in real time.
   3. Clicking a result → navigates to that PDP (standard link behavior).
   4. Pressing Enter → fires the search-log beacon with the current
      persona context AND the actual persona-page landing_path (e.g.
      /pages/tammy-2-0), so the Data Logs tab records the query as
      happening on the persona page rather than on /search.

   Why this exists: the magnifying glass on the persona shell used to
   send visitors to /search, which (a) broke the persona-page experience
   and (b) recorded landing_path as /search instead of the persona page.
   Bryce wanted searches that happen on Tammy 2.0 to be attributed to
   Tammy 2.0's data logs both by persona_id AND by landing_path.

   Idempotent — safe to load twice. Self-contained: injects its own DOM
   + CSS on first open; doesn't depend on the persona-shell having any
   particular drawer markup beyond the existing magnifying-glass anchor. */

(function () {
  var INSTALLED_FLAG = "__viori_persona_search_drawer_installed__";
  if (window[INSTALLED_FLAG]) return;
  window[INSTALLED_FLAG] = true;

  // Anchors that should open the drawer instead of navigating. Matches
  // the persona-shell's existing search icon button (line 54 of
  // persona-shell.liquid: <a href="/search" class="sp-nav__icon-btn">).
  var TRIGGER_SELECTOR = [
    'a.sp-nav__icon-btn[href="/search"]',
    'a.sp-nav__icon-btn[href="/search/"]',
    'a.sp-nav__icon-btn[aria-label="Search"]',
  ].join(",");

  var WORKER_BASE = "https://api.viori.storepersona.com";
  var SUGGEST_ENDPOINT = "/search/suggest.json";
  var SUGGEST_LIMIT = 8;
  var DEBOUNCE_MS = 250;

  var DRAWER_HTML =
    '<div class="sp-search-drawer" id="sp-search-drawer" hidden>' +
      '<button type="button" class="sp-search-drawer__backdrop" aria-label="Close search"></button>' +
      '<div class="sp-search-drawer__panel" role="dialog" aria-label="Search products">' +
        '<div class="sp-search-drawer__head">' +
          '<input class="sp-search-drawer__input" type="search" autocomplete="off" placeholder="Search Viori products…" aria-label="Search query" />' +
          '<button type="button" class="sp-search-drawer__close" aria-label="Close search">✕</button>' +
        '</div>' +
        '<div class="sp-search-drawer__results" data-results>' +
          '<div class="sp-search-drawer__empty">Type to search</div>' +
        '</div>' +
      '</div>' +
    '</div>';

  // Inline CSS — Viori brand colors. Kept here (rather than in a separate
  // CSS file) so the drawer is self-contained with no extra request.
  var DRAWER_CSS = [
    '.sp-search-drawer { position: fixed; inset: 0; z-index: 99999; }',
    '.sp-search-drawer[hidden] { display: none; }',
    '.sp-search-drawer__backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.45); border: 0; cursor: pointer; padding: 0; }',
    '.sp-search-drawer__panel { position: relative; max-width: 620px; width: calc(100% - 32px); margin: 6vh auto 0; background: #fff; border-radius: 14px; max-height: 82vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.3); overflow: hidden; }',
    '@media (max-width: 600px) { .sp-search-drawer__panel { margin: 16px auto; width: calc(100% - 16px); max-height: calc(100vh - 32px); } }',
    '.sp-search-drawer__head { display: flex; padding: 12px; border-bottom: 1px solid #e3e3e8; gap: 8px; align-items: center; }',
    '.sp-search-drawer__input { flex: 1; padding: 12px 14px; font-size: 16px; border: 1px solid #e3e3e8; border-radius: 8px; outline: none; box-sizing: border-box; -webkit-appearance: none; appearance: none; }',
    '.sp-search-drawer__input:focus { border-color: #d90073; }',
    '.sp-search-drawer__close { background: transparent; border: 1px solid #e3e3e8; border-radius: 8px; width: 44px; height: 44px; cursor: pointer; font-size: 16px; flex-shrink: 0; color: #15151a; }',
    '.sp-search-drawer__close:hover { border-color: #d90073; color: #d90073; }',
    '.sp-search-drawer__results { padding: 8px; overflow-y: auto; flex: 1; }',
    '.sp-search-result { display: flex; gap: 14px; padding: 10px; border-radius: 10px; text-decoration: none; color: inherit; align-items: center; }',
    '.sp-search-result:hover { background: #f7f5f1; }',
    '.sp-search-result__img { width: 64px; height: 64px; border-radius: 8px; object-fit: cover; flex-shrink: 0; background: #f4f4f6; }',
    '.sp-search-result__body { flex: 1; min-width: 0; }',
    '.sp-search-result__title { font-weight: 600; font-size: 14px; margin: 0; color: #15151a; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; }',
    '.sp-search-result__price { font-size: 13px; color: #6b6b76; margin-top: 4px; }',
    '.sp-search-drawer__empty { text-align: center; padding: 32px 16px; color: #94949c; font-size: 14px; }',
    ".sp-search-drawer__empty strong { color: #15151a; font-weight: 600; }",
  ].join("\n");

  function injectStyles() {
    if (document.getElementById("sp-search-drawer-styles")) return;
    var style = document.createElement("style");
    style.id = "sp-search-drawer-styles";
    style.textContent = DRAWER_CSS;
    document.head.appendChild(style);
  }

  function ensureDrawer() {
    var existing = document.getElementById("sp-search-drawer");
    if (existing) return existing;
    var container = document.createElement("div");
    container.innerHTML = DRAWER_HTML;
    var drawer = container.firstElementChild;
    document.body.appendChild(drawer);
    bindDrawerEvents(drawer);
    return drawer;
  }

  function bindDrawerEvents(drawer) {
    var input = drawer.querySelector(".sp-search-drawer__input");
    var backdrop = drawer.querySelector(".sp-search-drawer__backdrop");
    var close = drawer.querySelector(".sp-search-drawer__close");
    var resultsEl = drawer.querySelector("[data-results]");

    backdrop.addEventListener("click", function () { closeDrawer(drawer); });
    close.addEventListener("click", function () { closeDrawer(drawer); });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !drawer.hidden) closeDrawer(drawer);
    });

    var fetchTimer = null;
    var inflightCtl = null;
    input.addEventListener("input", function () {
      if (fetchTimer) clearTimeout(fetchTimer);
      var q = input.value.trim();
      if (!q) {
        resultsEl.innerHTML = '<div class="sp-search-drawer__empty">Type to search</div>';
        return;
      }
      fetchTimer = setTimeout(function () { runQuery(q, resultsEl, function (ctl) { inflightCtl = ctl; }); }, DEBOUNCE_MS);
    });

    // Enter — definitive intent. Fire the beacon (this is the data point
    // we want logged) AND force a fresh results pull so the operator
    // sees their submit reflected.
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        var q = input.value.trim();
        if (!q) return;
        fireSearchBeacon(q);
        if (inflightCtl && inflightCtl.abort) try { inflightCtl.abort(); } catch (err) { /* swallow */ }
        runQuery(q, resultsEl, function (ctl) { inflightCtl = ctl; });
      }
    });
  }

  function openDrawer(drawer) {
    drawer.hidden = false;
    var input = drawer.querySelector(".sp-search-drawer__input");
    // Tiny delay so iOS Safari focuses on the input reliably.
    setTimeout(function () { if (input) { input.focus(); input.select(); } }, 60);
  }

  function closeDrawer(drawer) {
    drawer.hidden = true;
  }

  function runQuery(q, resultsEl, captureCtl) {
    resultsEl.innerHTML = '<div class="sp-search-drawer__empty">Searching…</div>';
    var url = SUGGEST_ENDPOINT +
      "?q=" + encodeURIComponent(q) +
      "&resources[type]=product" +
      "&resources[limit]=" + SUGGEST_LIMIT;
    var ctl = (typeof AbortController === "function") ? new AbortController() : null;
    captureCtl(ctl);
    fetch(url, { credentials: "same-origin", signal: ctl ? ctl.signal : undefined })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        if (products.length === 0) {
          resultsEl.innerHTML = '<div class="sp-search-drawer__empty">No matches for <strong>' + escapeHtml(q) + '</strong></div>';
          return;
        }
        var html = "";
        products.forEach(function (p) {
          var img = extractImageUrl(p);
          var title = String(p && p.title || "");
          var price = formatPrice(p);
          var url = extractProductUrl(p);
          html +=
            '<a class="sp-search-result" href="' + escapeAttr(url) + '">' +
              (img ? '<img class="sp-search-result__img" src="' + escapeAttr(img) + '" alt="" loading="lazy" />' : '<div class="sp-search-result__img"></div>') +
              '<div class="sp-search-result__body">' +
                '<p class="sp-search-result__title">' + escapeHtml(title) + '</p>' +
                '<div class="sp-search-result__price">' + escapeHtml(price) + '</div>' +
              '</div>' +
            '</a>';
        });
        resultsEl.innerHTML = html;
      })
      .catch(function (e) {
        // AbortError is fine — we cancel mid-flight when the user types.
        if (e && e.name === "AbortError") return;
        resultsEl.innerHTML = '<div class="sp-search-drawer__empty">Search failed. Try again.</div>';
      });
  }

  // Fire the search-log beacon. Posts directly via sendBeacon with the
  // SAME landing_path the visitor's currently on (e.g. /pages/tammy-2-0),
  // so the Data Logs tab sees the query as happening on the persona page
  // rather than on /search. Persona ID + session ID pulled from globals
  // installed by cust-persona-attribution.js + cust-persona-events.js.
  function fireSearchBeacon(q) {
    try {
      var persona = null;
      if (window.vioriPersona && typeof window.vioriPersona.get === "function") {
        try { persona = window.vioriPersona.get() || null; } catch (e) { /* swallow */ }
      }
      var session = null;
      if (window.vioriEvents && window.vioriEvents.sessionId) {
        session = window.vioriEvents.sessionId;
      }
      var body = JSON.stringify({
        query_text: q,
        persona_id: persona,
        session_id: session,
        landing_path: window.location.pathname,
        results_count: null,
      });
      var url = WORKER_BASE + "/api/landing/search-log";
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "text/plain" });
        navigator.sendBeacon(url, blob);
        return;
      }
      if (typeof fetch === "function") {
        fetch(url, {
          method: "POST",
          mode: "no-cors",
          keepalive: true,
          headers: { "Content-Type": "text/plain" },
          body: body,
        }).catch(function () { /* swallow */ });
      }
    } catch (e) { /* swallow — never block search UX on beacon failure */ }
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  // Shopify's predictive search returns featured image in a few different
  // shapes depending on theme config and API version. Handle them all so
  // we always render an image when one is available.
  // Shapes seen in the wild:
  //   - p.image = "https://cdn.shopify.com/..."          (string)
  //   - p.image = { src: "..." }                         (object)
  //   - p.featured_image = "..." or { src: "..." }       (object or string)
  //   - p.image_url = "..."                              (rare)
  //   - p.images = ["..."] or [{ src: "..." }]          (rare)
  function extractImageUrl(p) {
    if (!p) return "";
    if (typeof p.featured_image === "string" && p.featured_image) return p.featured_image;
    if (p.featured_image && typeof p.featured_image === "object") {
      return p.featured_image.src || p.featured_image.url || "";
    }
    if (typeof p.image === "string" && p.image) return p.image;
    if (p.image && typeof p.image === "object") {
      return p.image.src || p.image.url || "";
    }
    if (typeof p.image_url === "string" && p.image_url) return p.image_url;
    if (Array.isArray(p.images) && p.images.length > 0) {
      var first = p.images[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object") return first.src || first.url || "";
    }
    return "";
  }

  // Product URL also returned in different shapes by various predictive
  // search backends (Shopify native vs Search & Discovery vs third-party).
  // Handle string, object with .href / .url, or fall back to building from
  // a handle field. Returns "#" only if nothing usable was found — never
  // returns an object that would stringify to "[object Object]".
  function extractProductUrl(p) {
    if (!p) return "#";
    if (typeof p.url === "string" && p.url) return p.url;
    if (p.url && typeof p.url === "object") {
      var inner = p.url.href || p.url.url || p.url.path || "";
      if (typeof inner === "string" && inner) return inner;
    }
    if (typeof p.handle === "string" && p.handle) return "/products/" + p.handle;
    if (typeof p.product_url === "string" && p.product_url) return p.product_url;
    return "#";
  }

  // Price field is similarly inconsistent — sometimes a formatted string
  // ("$13.99"), sometimes a number in cents (1399). Format whichever we get.
  function formatPrice(p) {
    if (!p) return "";
    if (typeof p.price === "string" && p.price) return p.price;
    if (typeof p.price === "number") {
      // Shopify cents → dollars
      return "$" + (p.price / 100).toFixed(2);
    }
    if (p.price_min != null) {
      var n = typeof p.price_min === "number" ? p.price_min : parseFloat(p.price_min);
      if (!isNaN(n)) {
        return n > 100 ? "$" + (n / 100).toFixed(2) : "$" + n.toFixed(2);
      }
    }
    return "";
  }

  function init() {
    injectStyles();
    var triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    if (triggers.length === 0) return;
    var drawer = null;
    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        if (!drawer) drawer = ensureDrawer();
        openDrawer(drawer);
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
